/**
 * @prop --wf-header-bg-translucent: The header background.
 * @prop --wf-header-height: The header height.
 * @prop --wf-header-backdrop-filter: The header backdrop filter.
 * @prop --wf-header-overlay-background-color: The overlay background color.
 * @prop --wf-header-box-shadow: The box-shadow for the nav flyout and others.
 * @prop --animation-delay: The delay of the animations.
 * @prop --animation-duration: The duration of the animations.
  */

:host {
    position: sticky;
    top: 0;
    z-index: 3000;
    font-weight: normal;

    --_box-shadow: var(--wf-header-box-shadow, 0 8px 8px rgb(0 0 0 / 32%));
    --vx-drawer-max-width: 345px;
    --vx-drawer-box-shadow-size: 8px;
    --vx-drawer-box-shadow-color: rgb(0 0 0 / 32%);
    --vx-cart-box-shadow: var(--_box-shadow);
    --vx-global-search-suggestions-box-shadow: var(--_box-shadow);
    --animation-duration: 0.15s;
    --transition-duration: 0.15s;
    --animation-delay: .2s
}

@mixin blur-background {
    --_filter: var(--wf-header-backdrop-filter, (blur(5px) saturate(200%)));
    backdrop-filter: var(--_filter);
    background-color: var(--wf-header-background-color, #fff);
}

@keyframes fade-in {
    0% {
        opacity: 0;
    }

    100% {
        opacity: 1;
    }
}

@keyframes focus-animation {
    0% {
        box-shadow: none;
    }

    100% {
        box-shadow: var(--wf-header-box-shadow, 0 0 12px rgb(0 0 0 / 32%));
    }
}

@keyframes backdrop-animation {
    0% {
        background-color: transparent;
    }

    100% {
        background-color: var(--wf-header-overlay-background-color, var(--overlay-background, rgb(209 207 207 / 60%), rgb(0 0 0 / 20%)));
    }
}

@mixin box-shadow {
    box-shadow: var(--wf-header-box-shadow, 0 8px 8px rgb(0 0 0 / 32%));
}

@keyframes fade-in-out {
    0%, 100% {
        opacity: 0;
    }

    50% {
        opacity: 1;
    }
}

.icon-favorite {
    fill: var(--mdc-theme-accent);
    position: absolute;
    height: 22px;
    width: 22px;
    right: 0;
    top: -2px;
    opacity: 0;

    &.animate {
        animation: fade-in-out ease-in-out 1s forwards;
    }
}

.header-container {
    @include blur-background;
    transition: background-color ease-in-out var(--transition-duration);
    display: flex;
    height: var(--wf-header-height, 72px);
    width: 100%;
    align-items: center;
    justify-content: center;

    &:hover {
        background-color: white;
    }

    .header-content {
        box-sizing: border-box;
        display: inline-flex;
        justify-content: space-between;
        align-items: center;
        gap: 8px;
        height: inherit;
        position: relative;
        width: 100vw;

        &.minimalistic {
            padding: 16px;
        }
    }

    [part="account-menu"] {
        position: relative;
        display: block;
    }
}

a {
    text-decoration: none;
    color: currentcolor;
}

img {
    display: block;
    max-width: 100%;
    max-height: 100%;
    height: auto;
}

.logo {
    margin-right: auto;
}

.actions {
    display: flex;
}

vx-mega-menu {
    display: none;
}

vx-side-nav {
    &::part(heading) {
        color: var(--mdc-theme-accent);
        font-size: 0.9em;
        font-weight: 800;
        border: none;
        margin-bottom: -8px;
    }

    &::part(menu-item) {
        background: transparent;
    }
}

vx-drawer.account-menu {
    z-index: 1;
}

vx-global-search {
    z-index: 2
}

vx-counter-badge {
    position: absolute;
    right: 1%;
}

.backdrop {
    position: fixed;
    top: 0;
    left: 0;
    width: 100vw;
    opacity: 0;
    z-index: -1;
    height: 0;
}

@media screen and (min-width: 1162px) {
    vx-side-nav {
        display: none;
    }

    .header-container {
        gap: 64px;
        background-color: var(--wf-header-bg-translucent, hsla(0deg 0% 100% / 90%));

        &:hover,
        &.is--sticky,
        &:focus-within {
            animation: focus-animation ease-in-out var(--animation-duration) forwards;

            vx-cart-overlay::part(wrapper) {
                border-color: transparent;
            }
        }

        &:hover ~ .backdrop {
            opacity: 1;
            height: 100vh;
            animation: fade-in ease-in-out forwards, backdrop-animation ease-in-out forwards;
            animation-duration: var(--animation-duration);
            animation-delay: var(--animation-delay);
        }

        .header-content {
            width: 100%;
            max-width: var(--page-width, 1440px);
            padding: 0 16px;
            gap: 32px;
        }
    }

    .logo {
        user-select: none;
        min-width: 200px;
        display: flex;
        align-items: center;
        height: inherit;
    }

    .desktop-menu {
        width: 100%;
        margin-top: 8px;
        overflow: hidden;
    }

    vx-mega-menu {
        display: flex;
    }

    vx-global-search {
        margin-right: 8px;
    }
}

